<template>
    <component :is="tag" class="list-block">
        <li v-for="(item, i) in data.items" :key="i" class="list-block-item">
            <a-dangerous-html :content="contentOf(item)" tag="span" />
            <component :is="tag" v-if="childrenOf(item).length" class="list-block">
                <li v-for="(child, j) in childrenOf(item)" :key="j" class="list-block-item">
                    <a-dangerous-html :content="contentOf(child)" tag="span" />
                </li>
            </component>
        </li>
    </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ADangerousHtml from '@core/components/dangerous-html/dangerous-html.vue';
import type { BodyVariant, ListData, ListItem } from '@model/editor-js/blocks';

defineOptions({
    name: 'BlockList',
});

const props = defineProps<{
    id?: string;
    data: ListData;
    variant: BodyVariant;
}>();

// `checklist` (legacy) renders as a plain unordered list
const tag = computed(() => (props.data.style === 'ordered' ? 'ol' : 'ul'));

// live content is at most two levels deep
const contentOf = (item: ListItem): string => (typeof item === 'string' ? item : item.content);
const childrenOf = (item: ListItem): ListItem[] => (typeof item === 'string' ? [] : item.items || []);
</script>

<style lang="pcss" scoped>
.variant-blog {
    .list-block {
        margin-top: 24px;
        margin-bottom: 24px;
        padding-inline-start: 16px;

        &:is(ol) {
            list-style-type: decimal;
            padding-inline-start: 16px;

            @media (--viewport-mobile-wide) {
                padding-inline-start: 32px;
            }

            .list-block-item {
                padding-inline-start: 3px;
            }
        }

        &:is(ul) {
            .list-block-item {
                position: relative;
                margin-bottom: 16px;

                &::before {
                    content: '';
                    display: inline-block;
                    height: 4px;
                    width: 4px;
                    background: var(--av-nav-secondary);
                    margin: 0 10px 0 10px;
                    position: absolute;
                    inset-inline-start: -24px;
                    top: 10px;
                    border-radius: 2px;
                }
            }
        }

        .list-block {
            margin-top: 16px;
            margin-bottom: 16px;
        }
    }

    .list-block-item {
        margin-bottom: 16px;
    }
}

.variant-tru {
    .list-block {
        margin-top: 24px;
        margin-bottom: 24px;

        @media (--viewport-mobile-wide) {
            padding-inline-start: 16px;
        }

        &:is(ol) {
            list-style-type: decimal;
            padding-inline-start: 16px;

            @media (--viewport-mobile-wide) {
                padding-inline-start: 32px;
            }

            .list-block-item {
                padding-inline-start: 3px;
            }
        }

        &:is(ul) {
            .list-block-item {
                position: relative;
                margin-bottom: 16px;

                &::before {
                    content: '';
                    display: inline-block;
                    height: 4px;
                    width: 4px;
                    background: var(--av-nav-secondary);
                    margin: 0 10px 0 10px;
                    position: absolute;
                    inset-inline-start: -24px;
                    top: 10px;
                    border-radius: 2px;
                }
            }
        }

        .list-block {
            margin-top: 16px;
            margin-bottom: 16px;
        }
    }

    .list-block-item {
        margin-bottom: 16px;
    }
}

.variant-resource .list-block {
    margin-top: 16px;
    padding-inline-start: 18px;
    text-align: start;
    color: var(--av-fixed-primary);

    .list-block-item {
        margin-bottom: 16px;
    }

    .list-block {
        margin-top: 16px;
    }
}

.variant-legal .list-block {
    padding-inline-start: 18px;
    text-align: start;
    color: var(--av-fixed-primary);

    .list-block {
        margin-top: 16px;
    }
}

.variant-resource,
.variant-legal {
    ol.list-block {
        list-style: decimal;
    }

    ul.list-block {
        list-style: square;
    }
}

.variant-event-about .list-block {
    padding-inline-start: 24px;

    &:is(ol) {
        margin-bottom: 24px;
        list-style-type: decimal;

        .list-block-item {
            margin-bottom: 16px;
        }
    }

    &:is(ul) {
        margin-bottom: 24px;

        .list-block-item {
            position: relative;
            margin-bottom: 16px;

            &::before {
                content: '';
                display: inline-block;
                height: 4px;
                width: 4px;
                background: var(--av-fixed-secondary);
                margin: 0 10px 0 10px;
                position: absolute;
                inset-inline-start: -24px;
                top: 10px;
                border-radius: 2px;
            }
        }
    }

    .list-block {
        margin-top: 16px;
        margin-bottom: 16px;
    }
}

.variant-event-compact .list-block {
    margin-bottom: 16px;

    &:is(ol) {
        list-style-type: decimal;
        padding-inline-start: 16px;

        @media (--viewport-mobile-wide) {
            padding-inline-start: 32px;
        }
    }

    &:is(ul) {
        .list-block-item {
            margin-bottom: 16px;

            &::before {
                content: '';
                display: inline-block;
                height: 3px;
                width: 3px;
                background: var(--av-fixed-secondary);
                margin: 0 10px 0 6px;
                position: relative;
                top: -3px;
            }
        }
    }

    .list-block {
        padding-inline-start: 16px;
        margin-top: 16px;
    }
}
</style>
